import { CollectionSVG, type SvgComponentType } from "@newsela/angelou";

import { useNavigationBarV2Context } from "@/base/NewselaGlobalLayoutV2/NavigationBar/NavigationBar.provider";
import {
  leftContainerCss,
  libraryMenuPopoutContainer,
  rightContainerCss,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/ResourceMenus/ContentLibraryMenu/ContentLibraryPopout/ContentLibraryPopout.styles";
import {
  EXPLORE_CONTENT_ITEMS,
  MY_CONTENT_ITEMS,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/constants";
import {
  IMRA_TEACHER_BUNDLE_HREF,
  IMRA_STUDENT_BUNDLE_HREF,
  IMRA_TEACHER_BUNDLE_TITLE,
  IMRA_STUDENT_BUNDLE_TITLE,
} from "@/imra/constants";
import useIsImraUser from "@/imra/useIsImraUser";
import { PopoutMenuSection } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/PopoutMenuSection/PopoutMenuSection";
import { PopoutSectionItem } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/PopoutSectionItem/PopoutSectionItem";
import {
  PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE,
  USER_FEATURE_FOR_PROMOTED_AT_YOUR_DISTRICT_PAGE,
  USER_FEATURE_FOR_PROMOTED_AT_YOUR_SCHOOL_PAGE,
} from "@/components/ContentPromotions/constants";
import useHasFeature from "@/features/useHasFeature";
import useCanAccessStandardsAlignmentReport from "@/state-standards/useCanAccessStandardsAlignmentReport";

import useGetUserProperties from "@/users/v3/useGetUserProperties";
import useUserFeaturesQuery from "@/users/v3/useUserFeaturesQuery";

const ContentLibraryPopout = () => {
  const {
    getters: { customCollections, personalizedCollections },
  } = useNavigationBarV2Context();

  const { data: features } = useUserFeaturesQuery();

  const { isStudent, isLiteUser } = useGetUserProperties();

  const { canAccessStandardsAlignmentReport } =
    useCanAccessStandardsAlignmentReport();

  const isImraUser = useIsImraUser();

  const [hasUserFeatureToAccessPromotedAtYourDistrictPage] = useHasFeature(
    USER_FEATURE_FOR_PROMOTED_AT_YOUR_DISTRICT_PAGE,
  );
  const [hasUserFeatureToAccessPromotedAtYourSchoolPage] = useHasFeature(
    USER_FEATURE_FOR_PROMOTED_AT_YOUR_SCHOOL_PAGE,
  );

  const activeStemLicense = features?.includes("has_stem_license");
  const activeELALicense = features?.includes("has_ela_license");
  const canSeeGenerationGenius =
    activeStemLicense || activeELALicense || isLiteUser;

  return (
    <div css={libraryMenuPopoutContainer}>
      <div css={leftContainerCss}>
        <PopoutMenuSection title="My content" width="14.375rem">
          {MY_CONTENT_ITEMS.filter(
            (item) =>
              !(
                item.href === PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.district &&
                !hasUserFeatureToAccessPromotedAtYourDistrictPage
              ),
          )
            .filter(
              (item) =>
                !(
                  item.href === PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.school &&
                  !hasUserFeatureToAccessPromotedAtYourSchoolPage
                ),
            )
            .filter((item) => !isStudent || item.showForStudents)
            .map(({ title, href, SvgComponent, dataQASelector }) => (
              <PopoutSectionItem
                key={title}
                href={href}
                title={title}
                SvgComponent={SvgComponent}
                dataQASelector={dataQASelector}
              />
            ))}
          {personalizedCollections.map(({ title, href }) => (
            <PopoutSectionItem
              key={title}
              href={href}
              title={title}
              SvgComponent={CollectionSVG as SvgComponentType}
              dataQASelector={
                title
                  ? `global-nav-${title.toLowerCase().replaceAll(" ", "-")}`
                  : ""
              }
            />
          ))}
          {customCollections.map(({ title, href }) => (
            <PopoutSectionItem
              key={title}
              href={href}
              title={title}
              SvgComponent={CollectionSVG as SvgComponentType}
              dataQASelector={
                title
                  ? `global-nav-${title.toLowerCase().replaceAll(" ", "-")}`
                  : ""
              }
            />
          ))}
          {isImraUser && (
            <PopoutSectionItem
              href={
                isStudent ? IMRA_STUDENT_BUNDLE_HREF : IMRA_TEACHER_BUNDLE_HREF
              }
              title={
                isStudent
                  ? IMRA_STUDENT_BUNDLE_TITLE
                  : IMRA_TEACHER_BUNDLE_TITLE
              }
              SvgComponent={CollectionSVG as SvgComponentType}
              dataQASelector="global-nav-rhino-bundle"
            />
          )}
        </PopoutMenuSection>
      </div>

      {!isImraUser && (
        <div css={rightContainerCss}>
          <PopoutMenuSection title="Explore content" width="14.375rem">
            {EXPLORE_CONTENT_ITEMS.filter((item) => {
              if (item.isGenerationGenius) {
                return canSeeGenerationGenius;
              }
              return (
                (!isStudent || item.showForStudents) &&
                (!isLiteUser || item.showForLite)
              );
            }).map(
              (
                {
                  title,
                  href,
                  standardsAlignmentReportHref,
                  SvgComponent,
                  dataQASelector,
                  openInNewTab,
                  isGenerationGenius,
                },
                index,
              ) => (
                <PopoutSectionItem
                  key={index}
                  href={
                    canAccessStandardsAlignmentReport &&
                    standardsAlignmentReportHref
                      ? standardsAlignmentReportHref
                      : href
                  }
                  title={title}
                  openInNewTab={openInNewTab}
                  SvgComponent={
                    isGenerationGenius
                      ? (props: any) => <SvgComponent {...props} size={24} />
                      : SvgComponent
                  }
                  dataQASelector={dataQASelector}
                />
              ),
            )}
          </PopoutMenuSection>
        </div>
      )}
    </div>
  );
};

ContentLibraryPopout.displayName = "ContentLibraryPopout";
export { ContentLibraryPopout };
